import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { TipoProduto } from "@/types/tipo-produto";
import { formatCurrency } from "@/lib/utils";

interface TipoProdutosTableProps {
  produtos: TipoProduto[];
  onView: (produto: TipoProduto) => void;
  onEdit: (produto: TipoProduto) => void;
  onDelete: (produto: TipoProduto) => void;
  canEdit: boolean;
  canDelete: boolean;
}

export function TipoProdutosTable({
  produtos,
  onView,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}: TipoProdutosTableProps) {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof TipoProduto;
    direction: "asc" | "desc";
  }>({ key: "nome", direction: "asc" });

  const handleSort = (key: keyof TipoProduto) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedProdutos = [...produtos].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const getSortIcon = (key: keyof TipoProduto) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4" />;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };


  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
      <Table className="min-w-[900px]">
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer select-none hover:bg-muted/50"
              onClick={() => handleSort("nome")}
            >
              <div className="flex items-center gap-2">
                Nome {getSortIcon("nome")}
              </div>
            </TableHead>
              <TableHead
                className="cursor-pointer select-none hover:bg-muted/50"
                onClick={() => handleSort("categoria")}
              >
                <div className="flex items-center gap-2">
                  Categoria {getSortIcon("categoria")}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer select-none hover:bg-muted/50"
                onClick={() => handleSort("preco_base")}
              >
              <div className="flex items-center gap-2">
                Preço Base {getSortIcon("preco_base")}
              </div>
            </TableHead>
            <TableHead className="min-w-[100px]">Unidade</TableHead>
            <TableHead
              className="cursor-pointer select-none hover:bg-muted/50 min-w-[120px]"
              onClick={() => handleSort("tamanho")}
            >
              <div className="flex items-center gap-2">
                Tamanho {getSortIcon("tamanho")}
              </div>
            </TableHead>
            <TableHead className="text-right min-w-[120px]">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedProdutos.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center text-muted-foreground">
              Nenhum produto encontrado
            </TableCell>
          </TableRow>
          ) : (
            sortedProdutos.map((produto) => (
                <TableRow key={produto.id_tipo_produto}>
                  <TableCell className="font-medium min-w-[200px]">{produto.nome}</TableCell>
              <TableCell className="min-w-[150px]">{produto.categoria || "-"}</TableCell>
              <TableCell className="min-w-[120px]">{formatCurrency(produto.preco_base)}</TableCell>
                  <TableCell className="min-w-[100px]">{produto.unidade_venda}</TableCell>
                  <TableCell className="min-w-[120px]">{produto.tamanho || "-"}</TableCell>
                  <TableCell className="text-right min-w-[120px]">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onView(produto)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(produto)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(produto)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
